import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { InvitationsTable } from './InvitationsTable';
import { InviteMemberModal } from './InviteMemberModal';
import { useAccount } from '../../contexts/AccountContext';
import { useToast } from '../../contexts/ToastContext';
import { getHttpStatus, toUserErrorMessage } from '../../lib/apiError';
import { useInvitations } from '../../services/api/hooks/useInvitations';
import { useResendInvitation } from '../../services/api/hooks/useResendInvitation';
import type { ExtranetInvitationOutput } from '../../types/extranet-api.types';
import { OffsetPagination } from '../common/OffsetPagination';

const DEFAULT_LIMIT = 20;

export const InvitationsTab = () => {
  const toast = useToast();
  const { isAdmin } = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const offset = Math.max(0, Number(searchParams.get('offset') ?? 0));
  const limit = Number(searchParams.get('limit') ?? DEFAULT_LIMIT);

  const query = useInvitations({ offset, limit });
  const resend = useResendInvitation();

  const updateParams = (mutate: (p: URLSearchParams) => void) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      mutate(next);
      return next;
    });

  const handleResend = (invitation: ExtranetInvitationOutput) => {
    setResendingId(invitation.invitationId);
    resend.mutate(invitation.invitationId, {
      onSuccess: () => toast.success('Invitation renvoyée', `Un nouvel email a été envoyé à ${invitation.email}.`),
      onError: err => {
        const { title, description } = toUserErrorMessage(err, 'Échec du renvoi');
        toast.error(title, description);
      },
      onSettled: () => setResendingId(null),
    });
  };

  // Lister les invitations du compte est un droit d'admin ; inviter n'en est
  // pas un. Un 403 sur la liste ne doit donc pas ressembler à une panne pour un
  // membre qui a bel et bien le droit d'inviter.
  const listForbidden = query.isError && getHttpStatus(query.error) === 403;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-zinc-500">
          Un membre invité reçoit un email pour choisir son mot de passe. Ses rôles sont fixés à l&apos;invitation.
          {isAdmin ? null : ' Vous ne pouvez inviter que sur vos propres rôles et périmètres.'}
        </p>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="h-8 px-3 text-xs font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors duration-100 whitespace-nowrap"
        >
          + Inviter un membre
        </button>
      </div>

      {listForbidden ? (
        <p className="text-sm text-zinc-500 border border-zinc-200 rounded-md px-4 py-6 text-center">
          Seul un administrateur peut consulter les invitations en cours du compte. Vous pouvez malgré tout inviter un
          membre.
        </p>
      ) : (
        <>
          <InvitationsTable
            items={query.data?.items ?? []}
            isLoading={query.isLoading}
            isFetching={query.isFetching}
            isError={query.isError}
            error={query.error}
            onResend={handleResend}
            resendingId={resendingId}
          />

          <OffsetPagination
            offset={offset}
            limit={limit}
            pageSize={query.data?.items?.length ?? 0}
            total={query.data?.totalItems ?? 0}
            onOffsetChange={next => updateParams(p => p.set('offset', String(next)))}
            onLimitChange={next =>
              updateParams(p => {
                p.set('limit', String(next));
                p.set('offset', '0');
              })
            }
          />
        </>
      )}

      <InviteMemberModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
};
